import { useState, useEffect, useContext } from "react";
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  styled,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  Alert,
  Collapse,
} from "@mui/material";
import { useCheckoutSimplifiedUI } from "../../CheckoutSimplifiedUIProvider";
import { UserLoginContext } from "@/components/pages/Auth/UserLoginProvider";
import { formatPhone, validatePhone } from "@/utils/StringUtils";
import { reloadProfile } from "@/redux/actions";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { brandclub_colors } from "@brandclub/common-ui";
import OTPVerificationInput from "../../../Checkout/CheckoutForm/components/signIn/OTPVerificationInput";

const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "0",
  paddingBottom: "8px",
});

const FormRow = styled(Box)({
  display: "flex",
  gap: "16px",
  "@media (max-width: 768px)": {
    flexDirection: "column",
  },
});

const RememberMeBox = styled(Box)(({ theme }) => ({
  padding: "0",
  backgroundColor: "transparent",
}));

const DisclaimerText = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  color: theme.palette.text.secondary,
  marginTop: "8px",
  lineHeight: 1.5,
  a: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const OTPDialogContent = styled(DialogContent)({
  padding: "24px",
  minWidth: "400px",
  "@media (max-width: 768px)": {
    minWidth: "300px",
  },
});

interface RememberMeSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

export const RememberMeSection = ({
  isOpen,
  onToggle,
  disabled = false,
}: RememberMeSectionProps) => {
  const { session, updateBuyer, rememberSession } = useCheckoutSimplifiedUI();
  const userProfile = useAppSelector((state) => state.userProfile);
  const dispatch = useAppDispatch();

  const [rememberMe, setRememberMe] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  // OTP state
  const [otpSession, setOtpSession] =
    useState<{ username: string } | undefined>(undefined);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);

  const {
    signIn,
    submittingSignIn,
    verifyOtp,
    resendOtp,
    otpError,
    clearOtpError,
    resendCoolDown,
  } = useContext(UserLoginContext);

  // Initialize from session buyer or shipping/billing address
  useEffect(() => {
    if (session?.buyer) {
      setFirstName(
        session.buyer.first_name ||
          (session.fulfillment_address?.name.split(" ")[0] as string) ||
          ""
      );
      setLastName(
        session.buyer.last_name ||
          (session.fulfillment_address?.name.split(" ")[1] as string) ||
          ""
      );
      // Only update phone number from session if we don't already have one
      // This prevents the session from overwriting user input during OTP flow
      setPhoneNumber((prev) => prev || session.buyer?.phone_number || "");
    } else if (session?.fulfillment_address) {
      // Infer from shipping address if no buyer info
      const nameParts = session.fulfillment_address.name.split(" ");
      if (nameParts.length > 1) {
        setFirstName(nameParts[0]);
        setLastName(nameParts.slice(1).join(" "));
      } else {
        setFirstName(session.fulfillment_address.name);
      }
    }
  }, [session?.buyer, session?.fulfillment_address]);

  // Check if user is already signed in
  useEffect(() => {
    if (userProfile?.userId) {
      setRememberMe(true);
    }
  }, [userProfile?.userId]);

  // Sync session with user profile when user signs in
  useEffect(() => {
    const syncSessionWithUser = async () => {
      if (
        userProfile?.userId &&
        session?.user_id &&
        userProfile.userId !== session.user_id
      ) {
        try {
          await rememberSession(userProfile.userId);
        } catch (error) {
          console.error("Failed to sync session with signed-in user:", error);
        }
      }
    };

    syncSessionWithUser();
  }, [userProfile?.userId, session?.user_id, rememberSession]);

  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);
    if (!checked) {
      // Clear OTP session when unchecked
      setOtpSession(undefined);
      setShowOTPDialog(false);
    }
  };

  const handleFieldBlur = async () => {
    // Only update buyer info when remember me is checked
    if (rememberMe && firstName && lastName) {
      try {
        // Update buyer with name and email only, NOT phone number yet
        await updateBuyer({
          first_name: firstName,
          last_name: lastName,
          email: session?.buyer?.email || "",
        });
      } catch (error) {
        console.error("Failed to update buyer:", error);
      }

      // Send OTP if phone is valid and not already signed in
      if (validatePhone(phoneNumber) && !userProfile?.userId) {
        const phoneE164 = formatPhone(phoneNumber, "e164");
        clearOtpError();
        signIn(phoneE164, false, (_, session) => {
          setOtpSession(session);
          setShowOTPDialog(true);
        });
      }
    }
  };

  const handleVerifyOTP = async () => {
    if (verificationCode.length === 6) {
      setIsVerifyingOTP(true);
      clearOtpError();

      await verifyOtp(
        verificationCode,
        otpSession,
        async () => {
          // Success - user is signed in, now update buyer with verified phone
          try {
            await updateBuyer({
              first_name: firstName,
              last_name: lastName,
              email: session?.buyer?.email || "",
              phone_number: phoneNumber,
            });
          } catch (error) {
            console.error("Failed to update buyer with phone:", error);
          }

          // Reload profile to get the newly signed-in user's information
          dispatch(reloadProfile());

          setIsVerifyingOTP(false);
          setShowOTPDialog(false);
          setVerificationCode("");
        },
        () => {
          // Error
          setIsVerifyingOTP(false);
        }
      );
    }
  };

  const handleResendOTP = () => {
    resendOtp(phoneNumber, false, (session) => {
      setOtpSession(session);
    });
  };

  const handleCloseOTP = () => {
    setShowOTPDialog(false);
    setVerificationCode("");
    clearOtpError();
  };

  // Auto-verify when 6 digits entered
  useEffect(() => {
    if (verificationCode.length === 6) {
      handleVerifyOTP();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode]);

  return (
    <>
      <Box
        sx={{
          padding: "20px 0",
          borderTop: `1px solid ${brandclub_colors.grayLight}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontSize: 18, fontWeight: 600, marginBottom: "12px" }}
        >
          Remember me
        </Typography>

        <FormContainer>
          <RememberMeBox>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => handleRememberMeChange(e.target.checked)}
                  disabled={disabled || !!userProfile?.userId}
                />
              }
              label={
                <Typography variant="body2">
                  {userProfile?.userId
                    ? `Signed in as ${userProfile.email}`
                    : "Save my information for a faster checkout"}
                </Typography>
              }
              sx={{ margin: 0, marginLeft: "-10px" }}
            />
          </RememberMeBox>

          <Collapse in={rememberMe && !userProfile?.userId} timeout={300}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                mt: 2,
              }}
            >
              <FormRow>
                <TextField
                  fullWidth
                  label="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onBlur={handleFieldBlur}
                  disabled={disabled}
                  required
                />
                <TextField
                  fullWidth
                  label="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={handleFieldBlur}
                  disabled={disabled}
                  required
                />
              </FormRow>

              <TextField
                fullWidth
                type="tel"
                label="Mobile phone number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onBlur={handleFieldBlur}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleFieldBlur();
                  }
                }}
                disabled={disabled || submittingSignIn}
                placeholder="+1 234-567-8900"
                required
                helperText={
                  submittingSignIn ? "Sending verification code..." : ""
                }
              />
            </Box>
          </Collapse>

          <DisclaimerText>🔒 Secure and encrypted</DisclaimerText>
        </FormContainer>
      </Box>

      {/* OTP Verification Dialog */}
      <Dialog
        open={showOTPDialog}
        onClose={handleCloseOTP}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Verify your phone number</Typography>
            <Button onClick={handleCloseOTP} sx={{ minWidth: "auto" }}>
              ✕
            </Button>
          </Box>
        </DialogTitle>
        <OTPDialogContent>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Enter the 6-digit code sent to
          </Typography>
          <Typography variant="body1" fontWeight={600} mb={3} color="primary">
            {formatPhone(phoneNumber, "national")}
          </Typography>

          <OTPVerificationInput
            value={verificationCode}
            onChange={(value: string) => setVerificationCode(value)}
            maxLength={6}
            error={!!otpError}
            disabled={isVerifyingOTP}
          />

          {otpError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {otpError}
            </Alert>
          )}

          <Box mt={3} display="flex" flexDirection="column" gap={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleVerifyOTP}
              disabled={verificationCode.length !== 6 || isVerifyingOTP}
            >
              {isVerifyingOTP ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Verifying...
                </>
              ) : (
                "Verify"
              )}
            </Button>

            <Button
              variant="text"
              fullWidth
              onClick={handleResendOTP}
              disabled={resendCoolDown > 0 || isVerifyingOTP}
            >
              {resendCoolDown > 0
                ? `Resend code in ${resendCoolDown}s`
                : "Resend code"}
            </Button>

            <Button
              variant="text"
              onClick={() => {
                setPhoneNumber("");
                setOtpSession(undefined);
                handleCloseOTP();
              }}
              disabled={isVerifyingOTP}
            >
              Change phone number
            </Button>
          </Box>
        </OTPDialogContent>
      </Dialog>
    </>
  );
};
